import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Mutation, Query } from 'react-apollo';
import gql from 'graphql-tag';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';

const styles = theme => ({
  content: {
    minWidth: 500,
    minHeight: 300,
  },
  recipientList: {
    maxHeight: 200,
    overflow: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  formControl: {
    minWidth: '100%',
    marginTop: theme.spacing(2),
  },
  successBox: {
    padding: theme.spacing(2),
    backgroundColor: '#4caf50',
    color: 'white',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  errorBox: {
    padding: theme.spacing(2),
    backgroundColor: '#f44336',
    color: 'white',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
});

const GET_TEMPLATES_QUERY = gql`
  query GetEmailTemplates {
    emailTemplates {
      id
      name
      subject
      category
    }
  }
`;

const SEND_EMAIL_MUTATION = gql`
  mutation SendEmail($templateId: ID!, $userIds: [ID!]!) {
    send_email(templateId: $templateId, userIds: $userIds) {
      sentCount
      errors
    }
  }
`;

const SEND_INVENTORY_USER_EMAIL_MUTATION = gql`
  mutation SendInventoryUserEmail($templateId: ID!, $inventoryUserIds: [ID!]!) {
    send_inventory_user_email(templateId: $templateId, inventoryUserIds: $inventoryUserIds) {
      sentCount
      errors
    }
  }
`;

const InventoryUserSendEmailDialogInner = ({
  classes,
  data,
  selected,
  loadBulkItemsList,
  sendToInventoryUserEmails,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [sendResult, setSendResult] = useState(null);

  const selectedUsers = useMemo(() => {
    if (!Array.isArray(data) || !Array.isArray(selected)) return [];
    return data.filter((u) => selected.includes(u.id));
  }, [data, selected]);

  const userIds = useMemo(() => {
    if (sendToInventoryUserEmails) return [];
    return selectedUsers
      .map((u) => u?.user?.id)
      .filter(Boolean)
      .map((id) => id.toString());
  }, [selectedUsers, sendToInventoryUserEmails]);

  const inventoryUserIds = useMemo(() => {
    if (!sendToInventoryUserEmails) return [];
    return selectedUsers.map((u) => u.id.toString());
  }, [selectedUsers, sendToInventoryUserEmails]);

  const skippedUsers = useMemo(() => {
    if (sendToInventoryUserEmails) return [];
    return selectedUsers.filter((u) => !u?.user?.id);
  }, [selectedUsers, sendToInventoryUserEmails]);

  const handleOpen = () => {
    setOpen(true);
    setSelectedTemplateId('');
    setSendResult(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTemplateId('');
    setSendResult(null);
  };

  const canSend =
    Boolean(selectedTemplateId) &&
    (sendToInventoryUserEmails ? inventoryUserIds.length > 0 : userIds.length > 0) &&
    true;

  return (
    <>
      <Tooltip title="Send Email">
        <IconButton onClick={handleOpen} color="inherit" aria-label="Send Email">
          <EmailIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Email to {selectedUsers.length} User{selectedUsers.length > 1 ? 's' : ''}
        </DialogTitle>

        <DialogContent className={classes.content}>
          {skippedUsers.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" color="error">
                {skippedUsers.length} selected inventory user(s) do not have an associated platform user
                account, so they will be skipped (to avoid emailing the wrong person).
              </Typography>
            </Box>
          )}

          <Typography variant="body2" gutterBottom>
            Recipients:
          </Typography>
          <div className={classes.recipientList}>
            <List dense>
              {selectedUsers.map((u) => {
                const isSendable = Boolean(u?.user?.id);
                return (
                  <ListItem key={u.id}>
                    <ListItemText
                      primary={`${u.name || 'Unknown'}${isSendable ? '' : ' (skipped)'}`}
                      secondary={u.email || 'No email'}
                    />
                  </ListItem>
                );
              })}
            </List>
          </div>

          <Query query={GET_TEMPLATES_QUERY}>
            {({ loading: loadingTemplates, error: templatesError, data: templatesData }) => (
              <>
                {templatesError && (
                  <Typography color="error">
                    Error loading templates: {templatesError.message}
                  </Typography>
                )}

                {loadingTemplates ? (
                  <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <FormControl className={classes.formControl} variant="outlined">
                    <InputLabel>Select Email Template</InputLabel>
                    <Select
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                      label="Select Email Template"
                    >
                      <MenuItem value="">
                        <em>-- Select a template --</em>
                      </MenuItem>
                      {templatesData?.emailTemplates?.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name} ({template.category})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </Query>

          {sendResult && (
            <Box
              className={
                sendResult.sentCount > 0 && (sendResult.errors || []).length === 0
                  ? classes.successBox
                  : classes.errorBox
              }
            >
              {sendResult.sentCount > 0 && (sendResult.errors || []).length === 0 ? (
                <Typography>
                  ✓ Email sent successfully to {sendResult.sentCount} user(s)!
                </Typography>
              ) : (
                <div>
                  {sendResult.sentCount > 0 && (
                    <Typography>✓ Email sent to {sendResult.sentCount} user(s)</Typography>
                  )}
                  {(sendResult.errors || []).length > 0 && (
                    <Typography>✗ Errors: {sendResult.errors.join(', ')}</Typography>
                  )}
                </div>
              )}
            </Box>
          )}
        </DialogContent>

        <Mutation
          mutation={sendToInventoryUserEmails ? SEND_INVENTORY_USER_EMAIL_MUTATION : SEND_EMAIL_MUTATION}
          onCompleted={(mutationData) => {
            const result = mutationData?.send_inventory_user_email || mutationData?.send_email;
            setSendResult(result);
            if (result?.sentCount > 0 && (result?.errors || []).length === 0) {
              setTimeout(() => {
                setOpen(false);
                setSelectedTemplateId('');
                setSendResult(null);
                if (loadBulkItemsList) loadBulkItemsList([]);
              }, 2000);
            }
          }}
          onError={(error) => {
            setSendResult({ sentCount: 0, errors: [error.message] });
          }}
        >
          {(sendEmail, { loading: sending }) => (
            <DialogActions>
              <Button onClick={handleClose} disabled={sending}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  sendEmail({
                    variables: {
                      templateId: selectedTemplateId,
                      ...(sendToInventoryUserEmails ? { inventoryUserIds } : { userIds }),
                    },
                  });
                }}
                color="primary"
                variant="contained"
                disabled={!canSend || sending}
                startIcon={sending ? <CircularProgress size={20} /> : <EmailIcon />}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogActions>
          )}
        </Mutation>
      </Dialog>
    </>
  );
};

InventoryUserSendEmailDialogInner.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.array,
  selected: PropTypes.array,
  loadBulkItemsList: PropTypes.func,
  sendToInventoryUserEmails: PropTypes.bool,
};

InventoryUserSendEmailDialogInner.defaultProps = {
  data: [],
  selected: [],
  loadBulkItemsList: null,
  sendToInventoryUserEmails: false,
};

const StyledInventoryUserSendEmailDialogInner = withStyles(styles)(InventoryUserSendEmailDialogInner);

const InventoryUserSendEmailDialog = (props) => <StyledInventoryUserSendEmailDialogInner {...props} />;

InventoryUserSendEmailDialog.propTypes = {
  data: PropTypes.array,
  selected: PropTypes.array,
  loadBulkItemsList: PropTypes.func,
  sendToInventoryUserEmails: PropTypes.bool,
};

InventoryUserSendEmailDialog.defaultProps = {
  data: [],
  selected: [],
  loadBulkItemsList: null,
};

export default InventoryUserSendEmailDialog;

